import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import isBefore from 'date-fns/isBefore'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Label } from 'components/v2/Label'
import { InputCurrency } from 'components/InputCurrency'
import { InputDateTime } from 'components/InputDateTime'
import { InputNumber } from 'components/InputNumber'
import { InputPercentage } from 'components/InputPercentage'
import { Radio } from 'components/v2/Radio'
import FormLayout from 'features/App/components/FormLayout'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { CampaignAutocomplete } from 'features/Campaigns/components/CampaignAutocomplete'
import { DISCOUNT_TYPE } from 'features/Coupons/components/DiscountType/constants'
import { schema } from './schema'
import { getUnprocessableErrors } from 'utils/error'
import { sanitizePrice, getValueByType } from 'utils/math'
import { GTCampaignArea } from 'gql/types.generated'
import { ReactElement } from 'react'
import { canSelectCompany, getDefaultCompanyForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'
import { isEmpty } from 'lodash'

const messages = defineMessages({
  companyPlaceholder: {
    id: 'companies.autocomplete.placeholder',
    defaultMessage: 'Select company'
  },
  campaignPlaceholder: {
    id: 'campaigns.autocomplete.placeholder',
    defaultMessage: 'Select campaign'
  },
  codePlaceholder: {
    id: 'dictionary.code',
    defaultMessage: 'Code'
  },
  startsAtPlaceholder: {
    id: 'coupons.form.startsAt.placeholder',
    defaultMessage: 'Select starting date'
  },
  endsAtPlaceholder: {
    id: 'coupons.form.endsAt.placeholder',
    defaultMessage: 'Select ending date'
  }
})

const getRandomCode = (): TSFixMe => {
  const randomStr = Math.random().toString(36)
  return randomStr.substring(randomStr.length - 6).toUpperCase()
}

const NewSnacksCouponForm = (props: TSFixMe): TSFixMe => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    setValues,
    handleBlur,
    handleSubmit,
    error,
    loading,
    isEdit,
    campaign
  } = props

  const intl = useIntl()
  const login = useLogin()

  const handleCompanyChange = (value: TSFixMe): TSFixMe => {
    setValues({
      ...values,
      company: value,
      campaign: {
        id: '',
        name: ''
      },
      constraints: {
        ...values.constraints,
        segments: []
      }
    })
  }

  const handleCampaignChange = (value: TSFixMe): TSFixMe => {
    setValues({
      ...values,
      campaign: value,
      startsAt: new Date(value.startsAt),
      endsAt: new Date(value.endsAt)
    })
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        {canSelectCompany(login.accessToken) && (
          <Row>
            <Column width="50%">
              <CompanyAutocomplete
                id="company.id"
                onChange={handleCompanyChange}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                disabled={!isEmpty(campaign) || isEdit}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column width="50%">
            <Input
              id="code"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.code}
              placeholder={intl.formatMessage(messages.codePlaceholder)}
              touched={touched.code}
              errors={errors.code}
              label={<FormattedMessage id="dictionary.code" defaultMessage="Code" />}
              readOnly={isEdit}
            />
          </Column>
          <Column width="50%">
            <Label>
              <FormattedMessage id="dictionary.campaign" defaultMessage="Campaign" />
            </Label>
            {canSelectCompany(login.accessToken) && !values.company.id && (
              <FormattedMessage id="coupons.snacks.form.company.empty" defaultMessage="Select company first" />
            )}
            {values.company.id && !(campaign && campaign.id) && (
              <CampaignAutocomplete
                id="campaign.id"
                onChange={handleCampaignChange}
                onBlur={handleBlur}
                value={values.campaign.name}
                placeholder={intl.formatMessage(messages.campaignPlaceholder)}
                touched={touched.campaign && touched.campaign.id}
                errors={errors.campaign && errors.campaign.id}
                companyId={values.company.id}
                filter={(campaign: TSFixMe): TSFixMe => {
                  return isBefore(new Date(), new Date(campaign.endsAt))
                }}
                areas={[GTCampaignArea.Snacks]}
              />
            )}
            {campaign?.id && <Input value={campaign.name} readOnly />}
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputDateTime
              id="startsAt"
              intl={intl}
              value={values.startsAt}
              placeholder={intl.formatMessage(messages.startsAtPlaceholder)}
              onChange={(value): TSFixMe => {
                setFieldValue('startsAt', value)

                /*@ts-expect-error ts(2322) */
                if (!values.endsAt || isBefore(values.endsAt, value)) {
                  /*@ts-expect-error ts(2322) */
                  setFieldValue('endsAt', endOfDay(value))
                }
              }}
              disabledDays={{
                before: values.campaign.id ? new Date(values.campaign.startsAt) : new Date(),
                after: values.campaign.id && new Date(values.campaign.endsAt)
              }}
              touched={touched.startsAt}
              errors={errors.startsAt}
              label={<FormattedMessage id="coupons.snacks.form.startsAt.label" defaultMessage="Starting date" />}
            />
          </Column>
          <Column width="50%">
            <InputDateTime
              id="endsAt"
              intl={intl}
              value={values.endsAt}
              placeholder={intl.formatMessage(messages.endsAtPlaceholder)}
              onChange={(value): TSFixMe => {
                setFieldValue('endsAt', value)
                /*@ts-expect-error ts(2322) */
                if (!values.startsAt || isBefore(value, values.startsAt)) {
                  /*@ts-expect-error ts(2322) */
                  setFieldValue('startsAt', startOfDay(value))
                }
              }}
              disabledDays={{
                before: values.campaign.id ? new Date(values.campaign.startsAt) : new Date(),
                after: values.campaign.id && new Date(values.campaign.endsAt)
              }}
              touched={touched.endsAt}
              errors={errors.endsAt}
              label={<FormattedMessage id="coupons.snacks.form.endsAt.label" defaultMessage="Ending date" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="coupons.snacks.form.discountType.label" defaultMessage="Discount type" />
            </Label>
            <Radio
              id={DISCOUNT_TYPE.PERCENTAGE}
              name="discountType"
              checked={values.discountType === DISCOUNT_TYPE.PERCENTAGE}
            >
              %
            </Radio>
          </Column>
          <Column>
            <label>
              <Label>
                <FormattedMessage id="dictionary.value" defaultMessage="Value" />
              </Label>
              <InputPercentage
                id="value"
                value={values.value}
                placeholder="0%"
                onChange={(value: TSFixMe): TSFixMe => {
                  setFieldValue('value', value)
                }}
                onBlur={handleBlur}
                touched={touched.value}
                errors={errors.value}
              />
            </label>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputCurrency
              id="constraints.minSale"
              value={values.constraints.minSale}
              placeholder="$0.00"
              onChange={(value): TSFixMe => {
                setFieldValue('constraints.minSale', value)
              }}
              label={
                <FormattedMessage
                  id="coupons.snacks.form.minSale"
                  defaultMessage="Minimum sale <small>(Optional)</small>"
                  values={{
                    small: (msg: TSFixMe): TSFixMe => <small>{msg}</small>
                  }}
                />
              }
              hideSuffix
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputNumber
              id="constraints.limit"
              value={values.constraints.limit}
              placeholder="0"
              onChange={(value: TSFixMe): TSFixMe => {
                setFieldValue('constraints.limit', value)
              }}
              onBlur={handleBlur}
              touched={touched.constraints && touched.constraints.limit}
              errors={errors.constraints && errors.constraints.limit}
              maskOptions={{
                suffix: '',
                prefix: '',
                allowDecimal: false
              }}
              label={<FormattedMessage id="coupons.snacks.form.limit" defaultMessage="Quantity (Limit)" />}
            />
          </Column>
        </Row>
      </FormLayout.CardContent>
      <FormLayout.Split />
      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="coupons.snacks.form.error"
                defaultMessage="There was an error while creating your coupon for snacks"
              />
            </ErrorMessage>
          </div>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/coupons/snacks" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewSnacksCoupon = ({ coupon = {}, campaign, onSubmit, error, loading, isEdit }: TSFixMe): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const initialCoupon = {
    code: coupon.code || getRandomCode(),
    company: coupon.campaign ? coupon.campaign.company : getDefaultCompanyForForm(login.accessToken),
    campaign: coupon.campaign && coupon.campaign.id ? coupon.campaign : { id: '', name: '' },
    startsAt: coupon.startsAt ? new Date(coupon.startsAt) : '',
    endsAt: coupon.endsAt ? new Date(coupon.endsAt) : '',
    discountType: coupon.discountType || DISCOUNT_TYPE.PERCENTAGE,
    value: getValueByType({
      value: coupon.value,
      isPercentage: coupon.discountType === DISCOUNT_TYPE.PERCENTAGE
    }),
    constraints: coupon.constraints
      ? {
          minSale: coupon.constraints.minSale ? sanitizePrice(coupon.constraints.minSale / 100) : '',
          limit: coupon.constraints.limit || ''
        }
      : {
          minSale: '',
          limit: ''
        }
  }

  return (
    <Formik initialValues={initialCoupon} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        return <NewSnacksCouponForm {...props} isEdit={isEdit} error={error} loading={loading} campaign={campaign} />
      }}
    </Formik>
  )
}
